/**
 * Virtual bank + crypto destination for Receive page.
 * Uses authenticated GET against the API when available; falls back to sample data if the route is missing.
 */

import { getAuthorizationHeader } from "./apiClient";
import { getApiV1Base } from "./apiConfig";

export const RECEIVE_FUNDING_FALLBACK = {
  bank: {
    accountName: "Michael Ellis",
    /** Shown as large title on Receive hub card (product / program name). */
    displayBrand: "FlowPay",
    /** Partner institution line under BANK PARTNER — from API when connected. */
    bankName: "",
    partnerBadge: "FDIC Insured",
    routingNumber: "021000021",
    accountType: "Checking",
    accountNumber: "884920008821",
    supportsLabel: "ACH · Wire Transfer",
  },
  crypto: {
    destinationAddress:
      "0x742d35Cc6634C0532925a3b844Bc454e4438f44e",
    assetLabel: "Ethereum (ETH)",
    settingsHref: "/crypto",
  },
  pdfDownloadPath: null,
};

function normalizePayload(json) {
  if (!json || typeof json !== "object") return { ...RECEIVE_FUNDING_FALLBACK };
  const b = json.bank && typeof json.bank === "object" ? json.bank : {};
  const c = json.crypto && typeof json.crypto === "object" ? json.crypto : {};
  return {
    bank: { ...RECEIVE_FUNDING_FALLBACK.bank, ...b },
    crypto: { ...RECEIVE_FUNDING_FALLBACK.crypto, ...c },
    pdfDownloadPath:
      json.pdfDownloadPath ?? json.pdfUrl ?? RECEIVE_FUNDING_FALLBACK.pdfDownloadPath,
  };
}

export async function fetchReceiveFunding(signal) {
  const base = getApiV1Base();
  const authHeaders = getAuthorizationHeader();

  try {
    const res = await fetch(`${base}/receive/funding`, {
      method: "GET",
      credentials: "include",
      headers: { Accept: "application/json", ...authHeaders },
      signal,
    });
    if (res.ok) {
      const json = await res.json();
      return normalizePayload(json);
    }
  } catch {
    /* network / CORS — use fallback */
  }

  await new Promise((r) => setTimeout(r, 450));
  return normalizePayload(RECEIVE_FUNDING_FALLBACK);
}

export function getReceiveFundingPdfUrl(data) {
  const base = getApiV1Base();
  if (!base || !data?.pdfDownloadPath) return null;
  if (/^https?:\/\//i.test(data.pdfDownloadPath)) return data.pdfDownloadPath;
  return `${base}${data.pdfDownloadPath.startsWith("/") ? "" : "/"}${data.pdfDownloadPath}`;
}

/** Last four digits for masked display; uses accountNumber if present. */
export function getAccountLastFour(bank) {
  if (!bank?.accountNumber) return "";
  const d = String(bank.accountNumber).replace(/\D/g, "");
  return d.length >= 4 ? d.slice(-4) : "";
}

export function formatBankCopyText(bank) {
  if (!bank) return "";
  return [
    `Account name: ${bank.accountName}`,
    `Bank: ${bank.bankName}`,
    `Routing: ${bank.routingNumber}`,
    `Account: ${bank.accountNumber}`,
    `Type: ${bank.accountType}`,
    `Supports: ${bank.supportsLabel}`,
  ].join("\n");
}
